// @flow
import {createSelector} from 'reselect';
import config from '../../config';
import {sortByDataVoiceSMS} from '../../lib/sort-options';
import type {StateType} from '../reducers';
import type {AvailableOptionStateType} from './reducer';

const EMPTY_OBJECT: {[string]: Object[]} = {};
const EMPTY_ARRAY: Object[] = [];
const EMPTY_AVAILABLE_OPTION_STATE: AvailableOptionStateType = ({}: any);

export const availableOptionStateSelector = (
  state: StateType,
): AvailableOptionStateType => state.availableOptions || EMPTY_AVAILABLE_OPTION_STATE;

export const availableCreditableOptionsSelector = (
  state: StateType,
): {[string]: Object[]} => {
  const availableOptionState = availableOptionStateSelector(state);
  return availableOptionState.creditableOptions || EMPTY_OBJECT;
};

const getBaseOptionsSelector = (state: StateType) =>
  config.displayRoamingPlanSliders
    ? localCreditableOptionsSelector(state)
    : availableCreditableOptionsSelector(state);

export const firstAvailableCreditableOptionsSelector = (
  state: StateType,
): Object[] => {
  const creditableOptions = getBaseOptionsSelector(state);
  return (Object.values(creditableOptions)/*: any */).map(options => options[0]).filter(Boolean);
};

export const firstAvailableCreditableOptionsForActivationOrderSelector = (
  state: StateType,
): Object[] => {
  const availableCreditableOptionsState = getBaseOptionsSelector(state);
  return (Object.values(availableCreditableOptionsState) /*: any */).map(
    creditableOptions =>
      creditableOptions.filter(option => {
        let isOK = true;
        option?.characteristics?.forEach(charac => {
          if (charac.name === 'ORDER_CATEGORY_RESTRICTED') {
            if (!charac?.value.includes('ACTIVATION')) {
              isOK = false;
            }
          }
        });
        return isOK;
      })[0],
  );
};

export const availableServiceOptionsSelector = (state: StateType): Object[] => {
  const availableOptionState = availableOptionStateSelector(state);
  return availableOptionState.serviceOptions || EMPTY_ARRAY;
};

export const getCreditableOptionsWithPlan: (state: StateType) => Object[][] =
  createSelector(
    availableCreditableOptionsSelector,
    creditableOptionsAvailable =>
      (Object.values(creditableOptionsAvailable): any).sort(
        (creditableOptions1, creditableOptions2) =>
          sortByDataVoiceSMS(
            creditableOptions1[0].type,
            creditableOptions2[0].type,
          ),
      ),
  );

// ─── Roaming / local split ──────────────────────────────────────────────────

export const isRoamingType = (type: string): boolean =>
  (config.roamingServiceSpecs || []).includes(type);

export const localCreditableOptionsSelector: (state: StateType) => {[string]: Object[]} =
  createSelector(
    availableCreditableOptionsSelector,
    (all) => {
      const result: {[string]: Object[]} = {};
      for (const key of Object.keys(all)) {
        if (!isRoamingType(key)) {
          result[key] = all[key];
        }
      }
      return result;
    },
  );

export const roamingCreditableOptionsSelector: (state: StateType) => {[string]: Object[]} =
  createSelector(
    availableCreditableOptionsSelector,
    (all) => {
      const result: {[string]: Object[]} = {};
      const dataSpecs = config.roamingDataServiceSpecs || [];
      const mergedDataOptions: Object[] = [];

      for (const key of Object.keys(all)) {
        if (!isRoamingType(key)) continue;
        if (dataSpecs.includes(key)) {
          mergedDataOptions.push(...all[key]);
        } else {
          result[key] = all[key];
        }
      }

      if (mergedDataOptions.length > 0) {
        result['ROA_DATA'] = mergedDataOptions;
      }
      return result;
    },
  );

export const hasRoamingCreditableOptions: (state: StateType) => boolean =
  createSelector(
    roamingCreditableOptionsSelector,
    (roaming) => Object.keys(roaming).length > 0,
  );

export const getLocalCreditableOptionsWithPlan: (state: StateType) => Object[][] =
  createSelector(
    localCreditableOptionsSelector,
    creditableOptionsAvailable =>
      (Object.values(creditableOptionsAvailable): any).sort(
        (creditableOptions1, creditableOptions2) =>
          sortByDataVoiceSMS(
            creditableOptions1[0].type,
            creditableOptions2[0].type,
          ),
      ),
  );

export const getRoamingCreditableOptionsWithPlan: (state: StateType) => Object[][] =
  createSelector(
    roamingCreditableOptionsSelector,
    creditableOptionsAvailable =>
      (Object.values(creditableOptionsAvailable): any).sort(
        (creditableOptions1, creditableOptions2) =>
          sortByDataVoiceSMS(
            creditableOptions1[0].type,
            creditableOptions2[0].type,
          ),
      ),
  );
